<script>
  import { thumb, getOptimizedUrl } from '../../lib/cloudinary.js';
  import { slugify } from '../../lib/slug.js';
  import { resolveColor, cleanHex } from '../../lib/colors.js';
  import ImageUpload from './ImageUpload.svelte';
  import Button from '../ui/Button.svelte';
  import Badge from '../ui/Badge.svelte';
  import Card from '../ui/Card.svelte';
  import Switch from '../ui/Switch.svelte';
  import Sheet from '../ui/Sheet.svelte';
  import DatePicker from '../ui/DatePicker.svelte';
  import TimePicker from '../ui/TimePicker.svelte';
  import { inputCls, textareaCls, selectCls, labelCls, hintCls, thCls, tdCls } from '../../lib/ui.js';
  import { toast } from '../../lib/toast.js';
  import { confirmDialog } from '../../lib/confirm.js';
  import Plus from 'lucide-svelte/icons/plus';
  import Pencil from 'lucide-svelte/icons/pencil';
  import Trash2 from 'lucide-svelte/icons/trash-2';
  import Search from 'lucide-svelte/icons/search';
  import ArrowLeft from 'lucide-svelte/icons/arrow-left';
  import LoaderCircle from 'lucide-svelte/icons/loader-circle';
  import Package from 'lucide-svelte/icons/package';
  import CalendarClock from 'lucide-svelte/icons/calendar-clock';
  import X from 'lucide-svelte/icons/x';

  let { products = [], categories = [], currency = 'R', timeZone = 'Africa/Johannesburg', onUpdate = () => {}, onLocalUpdate = null } = $props();
  // Release times are shown and entered in the shop's time zone (the server reads them that way), whatever the computer's own.
  const inShop = (d, opts) => new Date(d).toLocaleString('en-GB', { timeZone, ...opts });
  const shopDate = (d) => new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(d));
  const shopTime = (d) => inShop(d, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const shortWhen = (d) => inShop(d, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const syncLocal = (list) => (onLocalUpdate || onUpdate)(list);

  let editing = $state(null);
  let isNew = $state(false);
  let deletingId = $state(null);
  let saving = $state(false);
  let query = $state('');
  let snapshot = $state('');
  // A new product's web address follows its name until the admin edits the address by hand.
  let slugTouched = $state(false);
  let slugClash = $derived(editing && slugify(editing.slug) ? products.find(p => p.id !== editing.id && p.slug === slugify(editing.slug)) : null);
  const siteHost = typeof window !== 'undefined' ? window.location.host : '';

  const emptyProduct = () => ({
    id: '', slug: '', name: '', category: '', price: 0,
    image: '', images: [], colorImages: [],
    description: '',
    sizes: ['S', 'M', 'L', 'XL'], colors: [], colorSwatches: [], stock: 0, variants: [],
    isNew: false, isFeatured: false, hidden: false,
  });

  /** Build a stock row per size/color combination, preserving any stock already entered. */
  function buildVariants(sizes, colors, existing) {
    const sizeList = sizes?.length ? sizes : [''];
    const colorList = colors?.length ? colors : [''];
    const rows = [];
    for (const size of sizeList) {
      for (const color of colorList) {
        const prev = (existing || []).find(v => (v.size || '') === size && (v.color || '') === color);
        rows.push({ size, color, stock: prev ? prev.stock : 0 });
      }
    }
    return rows;
  }

  function variantStock(size, color) {
    const v = (editing?.variants || []).find(v => (v.size || '') === (size || '') && (v.color || '') === (color || ''));
    return v ? v.stock : 0;
  }

  function updateVariantStock(size, color, value) {
    const stock = Math.max(0, parseInt(value, 10) || 0);
    const variants = (editing.variants || []).map(v => ({ ...v }));
    const idx = variants.findIndex(v => (v.size || '') === (size || '') && (v.color || '') === (color || ''));
    if (idx >= 0) variants[idx].stock = stock;
    else variants.push({ size: size || '', color: color || '', stock });
    editing = { ...editing, variants };
  }

  let totalStock = $derived((editing?.variants || []).reduce((sum, v) => sum + (v.stock || 0), 0));
  let dirty = $derived(editing ? JSON.stringify(editing) !== snapshot : false);

  function colorImages(color) {
    return (editing?.colorImages || []).find(ci => ci.color === color)?.images || [];
  }
  function updateColorImages(color, urls) {
    const list = (editing.colorImages || []).filter(ci => ci.color !== color);
    if (urls.length) list.push({ color, images: urls });
    editing = { ...editing, colorImages: list };
  }

  function startEditing(p, creating) {
    const base = creating ? emptyProduct() : p;
    const sizes = base.sizes || [];
    const colors = base.colors || [];
    editing = {
      ...base,
      images: [...(base.images || [base.image].filter(Boolean))],
      colorImages: [...(base.colorImages || [])],
      hidden: Boolean(base.hidden),          // a switch cannot be bound to a missing value
      sizes, colors,
      variants: buildVariants(sizes, colors, base.variants || []),
    };
    isNew = creating;
    slugTouched = false;
    snapshot = JSON.stringify(editing);
  }

  function closeEditor() { editing = null; isNew = false; }

  async function requestClose() {
    if (dirty && !(await confirmDialog.ask({
      title: 'Discard changes?', description: 'You have unsaved changes to this product.',
      confirmLabel: 'Discard', destructive: true,
    }))) return;
    closeEditor();
  }

  async function handleSave() {
    if (!editing || saving) return;
    if (!editing.name.trim()) return toast.error('Give the product a name.');
    if (!editing.category) return toast.error('Choose a category.');
    if (!(Number(editing.price) >= 0)) return toast.error('Enter a valid price.');
    saving = true;
    try {
      // Keep image and aggregate stock in sync with the edited fields
      const p = { ...editing, image: editing.images[0] || editing.image || '', stock: totalStock };
      if (isNew) {
        await onUpdate([...products, { ...p, id: `prod-${Date.now()}` }]);
      } else {
        await onUpdate(products.map(prod => prod.id === p.id ? p : prod));
      }
      closeEditor();
    } catch (e) {
      // Admin.svelte surfaces save failures via its own toast; keep the editor open.
    } finally {
      saving = false;
    }
  }

  async function handleDelete(id, name) {
    const ok = await confirmDialog.ask({
      title: `Delete “${name}”?`,
      description: 'The product and its uploaded images are permanently removed. This cannot be undone.',
      confirmLabel: 'Delete product', destructive: true,
    });
    if (!ok) return;
    deletingId = id;
    try {
      const res = await fetch(`/api/products/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Request failed');
      // Deletion (and its stock/asset cleanup) is already persisted server-side —
      // sync local state only, don't trigger another full-blob save.
      syncLocal(products.filter(p => p.id !== id));
      toast.success('Product deleted');
    } catch (e) {
      toast.error(`Failed to delete: ${e.message}`);
    } finally {
      deletingId = null;
    }
  }

  const categoryName = (id) => categories.find(c => c.id === id)?.name || id || '—';
  const money = (n) => `${currency}${Number(n || 0).toFixed(2)}`;

  function stockBadge(p) {
    if (p.stock === 0) return { label: 'Sold out', variant: 'destructive' };
    if (p.stock <= 10) return { label: `${p.stock} left`, variant: 'warning' };
    return { label: 'In stock', variant: 'success' };
  }

  const isScheduled = (p) => Boolean(p.publishAt) && new Date(p.publishAt) > new Date();
  let view = $state('all');
  const views = $derived([
    { value: 'all', label: 'All', n: products.length },
    { value: 'drafts', label: 'Hidden', n: products.filter(p => p.hidden).length },
    { value: 'scheduled', label: 'Scheduled', n: products.filter(isScheduled).length },
    { value: 'new', label: 'New', n: products.filter(p => p.isNew).length },
  ]);
  let filtered = $derived(products.filter(p => {
    const q = query.toLowerCase().trim();
    const inView = view === 'all' || (view === 'drafts' && p.hidden) || (view === 'scheduled' && isScheduled(p)) || (view === 'new' && p.isNew);
    return inView && (!q || p.name.toLowerCase().includes(q) || categoryName(p.category).toLowerCase().includes(q));
  }));

  // Choose products, then release them: on a date, right now, or back to a hidden draft.
  let picked = $state([]);
  const pickedSet = $derived(new Set(picked));
  const allPicked = $derived(filtered.length > 0 && filtered.every(p => pickedSet.has(p.id)));
  const toggle = (id) => (picked = pickedSet.has(id) ? picked.filter(x => x !== id) : [...picked, id]);
  const toggleAll = () => (picked = allPicked ? picked.filter(id => !filtered.some(p => p.id === id)) : [...new Set([...picked, ...filtered.map(p => p.id)])]);
  $effect(() => { const live = new Set(products.map(p => p.id)); if (picked.some(id => !live.has(id))) picked = picked.filter(id => live.has(id)); });

  let relOpen = $state(false);
  let relBusy = $state(false);
  let rel = $state({ date: '', time: '09:00', soon: true, feature: false });
  function openSchedule() {
    const when = products.find(p => pickedSet.has(p.id) && isScheduled(p));
    rel = when
      ? { date: shopDate(when.publishAt), time: shopTime(when.publishAt), soon: Boolean(when.comingSoon), feature: Boolean(when.featureOnRelease) }
      : { date: rel.date || shopDate(Date.now() + 86400000), time: rel.time, soon: rel.soon, feature: rel.feature };
    relOpen = true;
  }
  // Add the chosen products to a drop that has not launched yet.
  let dropOpen = $state(false);
  let dropList = $state(null);                  // null while loading
  let dropId = $state('');
  async function openDrops() {
    dropOpen = true; dropList = null; dropId = '';
    try {
      const res = await fetch('/api/drops', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not load drops.');
      dropList = data.drops.filter(d => ['planning', 'in_production', 'ready', 'live'].includes(d.status));
      dropId = dropList[0]?.id || '';
    } catch (e) { toast.error(e.message); dropOpen = false; }
  }
  async function addToDrop() {
    if (relBusy || !dropId || !picked.length) return;
    relBusy = true;
    try {
      const res = await fetch(`/api/drops/${dropId}/products`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ productIds: picked }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Request failed');
      const fresh = await fetch('/api/products', { credentials: 'include' }).then(r => (r.ok ? r.json() : null));
      if (fresh) syncLocal(fresh);
      toast.success(`${picked.length} product${picked.length === 1 ? '' : 's'} added to “${data.name}”. ${data.status === 'live' ? 'On sale now, tagged New.' : `Hidden until ${data.launchDate} at ${data.launchTime || '09:00'}.`}`);
      picked = []; dropOpen = false;
    } catch (e) { toast.error(e.message); }
    finally { relBusy = false; }
  }
  async function release(mode) {
    if (relBusy || !picked.length) return;
    relBusy = true;
    try {
      const res = await fetch('/api/products/release', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: picked, mode, date: rel.date, time: rel.time, comingSoon: rel.soon, feature: mode === 'schedule' && rel.feature }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Request failed');
      const by = new Map(data.products.map(p => [p.id, p]));
      syncLocal(products.map(p => (by.has(p.id) ? { ...p, ...by.get(p.id) } : p)));
      const n = picked.length - data.skipped;
      const plural = n === 1 ? '' : 's';
      toast.success(mode === 'schedule' ? `${n} product${plural} scheduled for ${rel.date} at ${rel.time}.` : mode === 'now' ? `${n} product${plural} on sale now.` : `${n} product${plural} hidden from the shop.`);
      if (data.skipped) toast.error(`${data.skipped} left alone: a drop in Drops is holding ${data.skipped === 1 ? 'it' : 'them'}.`);
      picked = []; relOpen = false;
    } catch (e) { toast.error(e.message); }
    finally { relBusy = false; }
  }

  let sizesStr = $derived(editing ? editing.sizes.join(', ') : '');
  let colorsStr = $derived(editing ? editing.colors.join(', ') : '');

  function updateSizes(e) {
    const sizes = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
    editing = { ...editing, sizes, variants: buildVariants(sizes, editing.colors, editing.variants) };
  }
  // The colour each name is drawn as: what the admin picked, else our best guess from the name (so most are right already).
  const swatchOf = (color) => resolveColor(color, editing?.colorSwatches);
  const solidHex = (color) => { const v = swatchOf(color); return /^#[0-9a-f]{6}$/i.test(v) ? v : '#cccccc'; };
  function setSwatch(color, value) {
    const hex = cleanHex(value);
    const list = (editing.colorSwatches || []).filter(s => s.color !== color);
    if (hex) list.push({ color, hex });
    editing = { ...editing, colorSwatches: list };
  }
  function updateColors(e) {
    const colors = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
    // Drop per-color image sets for colors that no longer exist on this product.
    const colorImgs = (editing.colorImages || []).filter(ci => colors.includes(ci.color));
    const swatches = (editing.colorSwatches || []).filter(s => colors.includes(s.color));
    editing = { ...editing, colors, colorImages: colorImgs, colorSwatches: swatches, variants: buildVariants(editing.sizes, colors, editing.variants) };
  }
</script>

{#if !editing}
  <div class="space-y-6">
    <div class="flex items-end justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold tracking-tight">Products</h1>
        <p class="text-sm text-muted-foreground">{products.length} product{products.length === 1 ? '' : 's'} in your catalogue.</p>
      </div>
      <Button onclick={() => startEditing(null, true)}><Plus size={16} /> Add product</Button>
    </div>

    <div class="relative max-w-sm">
      <Search size={15} class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input type="search" bind:value={query} placeholder="Search products or categories" aria-label="Search products" class="{inputCls} pl-9" />
    </div>

    <div class="flex flex-wrap gap-1.5" role="group" aria-label="Show products">
      {#each views as v (v.value)}
        <button type="button" aria-pressed={view === v.value} onclick={() => (view = v.value)}
          class="rounded-full border px-3 py-1 text-sm transition-colors {view === v.value ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground hover:bg-muted/50'}">{v.label} <span class="tabular-nums opacity-70">{v.n}</span></button>
      {/each}
    </div>

    <Card class="overflow-hidden">
      {#if filtered.length === 0}
        <div class="flex flex-col items-center gap-2 py-16 text-center">
          <Package size={28} class="text-muted-foreground" />
          <p class="font-medium">{products.length ? 'No matching products' : 'No products yet'}</p>
          <p class="text-sm text-muted-foreground">{products.length ? 'Try a different search or filter.' : 'Add your first product to start selling.'}</p>
        </div>
      {:else}
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="border-b border-border">
              <tr>
                <th class="{thCls} w-10"><input type="checkbox" checked={allPicked} onchange={toggleAll} aria-label="Select all shown products" class="h-4 w-4 cursor-pointer" /></th>
                <th class={thCls}>Product</th>
                <th class="{thCls} hidden md:table-cell">Category</th>
                <th class="{thCls} text-right">Price</th>
                <th class="{thCls} text-right">Stock</th>
                <th class={thCls}>Status</th>
                <th class="{thCls} w-24 text-right"><span class="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {#each filtered as p (p.id)}
                {@const sb = stockBadge(p)}
                <tr class="border-b border-border/60 transition-colors last:border-0 hover:bg-muted/50 {pickedSet.has(p.id) ? 'bg-muted/40' : ''}">
                  <td class={tdCls}><input type="checkbox" checked={pickedSet.has(p.id)} onchange={() => toggle(p.id)} aria-label="Select {p.name}" class="h-4 w-4 cursor-pointer" /></td>
                  <td class={tdCls}>
                    <button type="button" class="flex items-center gap-3 text-left" onclick={() => startEditing(p, false)}>
                      {#if p.image || p.images?.[0]}
                        <img src={thumb(p.image || p.images?.[0], 40)} loading="lazy" decoding="async" alt="" class="h-10 w-10 shrink-0 rounded-md bg-muted object-cover" />
                      {:else}
                        <div class="h-10 w-10 shrink-0 rounded-md bg-muted"></div>
                      {/if}
                      <span>
                        <span class="block font-medium">{p.name}</span>
                        {#if p.hidden && !p.publishAtDropId}<span class="mt-0.5 flex items-center gap-1 text-xs font-medium text-muted-foreground">Hidden draft</span>{/if}
                        {#if isScheduled(p)}<span class="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><CalendarClock size={12} /> {p.comingSoon ? 'Coming soon, on sale' : 'Hidden until'} {shortWhen(p.publishAt)}{p.featureOnRelease ? ' · then featured' : ''}</span>{/if}
                        <span class="text-xs text-muted-foreground">{(p.images || []).length} image{(p.images || []).length !== 1 ? 's' : ''}{p.isNew ? ' · New' : ''}{p.isFeatured ? ' · Featured' : ''}</span>
                      </span>
                    </button>
                  </td>
                  <td class="{tdCls} hidden text-muted-foreground md:table-cell">{categoryName(p.category)}</td>
                  <td class="{tdCls} text-right tabular-nums">{money(p.price)}</td>
                  <td class="{tdCls} text-right tabular-nums">{p.stock}</td>
                  <td class={tdCls}><Badge variant={sb.variant}>{sb.label}</Badge></td>
                  <td class="{tdCls} text-right">
                    <div class="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" aria-label="Edit {p.name}" onclick={() => startEditing(p, false)}><Pencil size={15} /></Button>
                      <Button variant="ghost" size="icon" aria-label="Delete {p.name}" class="hover:text-destructive" disabled={deletingId === p.id} onclick={() => handleDelete(p.id, p.name)}>
                        {#if deletingId === p.id}<LoaderCircle size={15} class="animate-spin" />{:else}<Trash2 size={15} />{/if}
                      </Button>
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </Card>
  </div>

  {#if picked.length}
    <div class="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 backdrop-blur-sm md:left-60" role="region" aria-label="Selected products">
      <div class="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-8">
        <p class="text-sm font-medium">{picked.length} selected</p>
        <div class="flex flex-wrap items-center gap-2">
          <Button onclick={openSchedule}><CalendarClock size={15} /> Schedule release</Button>
          <Button variant="outline" disabled={relBusy} onclick={openDrops}>Add to drop</Button>
          <Button variant="outline" disabled={relBusy} onclick={() => release('now')}>Put on sale now</Button>
          <Button variant="outline" disabled={relBusy} onclick={() => release('cancel')}>Hide</Button>
          <Button variant="ghost" size="icon" aria-label="Clear selection" onclick={() => (picked = [])}><X size={16} /></Button>
        </div>
      </div>
    </div>
  {/if}

  <Sheet bind:open={dropOpen} title="Add to a drop" description="Scheduled drop: they stay hidden until it launches. Live drop: they go on sale straight away.">
    {#if dropList === null}
      <div class="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground" role="status"><LoaderCircle size={16} class="animate-spin" /> Loading drops…</div>
    {:else if dropList.length === 0}
      <p class="py-8 text-center text-sm text-muted-foreground">There are no upcoming drops. Create one in the Drop planner, or use Schedule release instead.</p>
    {:else}
      <div class="space-y-4">
        <div class="rounded-md border border-border">
          {#each dropList as d (d.id)}
            <label class="flex cursor-pointer items-center gap-3 border-b border-border/60 px-3 py-3 text-sm last:border-0 hover:bg-muted/40">
              <input type="radio" name="add-drop" value={d.id} bind:group={dropId} />
              <span class="min-w-0 flex-1"><span class="block truncate font-medium">{d.name}</span><span class="text-xs text-muted-foreground">{d.status === 'live' ? 'Live now: on sale at once' : `${d.launchDate} at ${d.launchTime || '09:00'}`} · {d.styles.length} product{d.styles.length === 1 ? '' : 's'}</span></span>
            </label>
          {/each}
        </div>
        <div class="flex justify-end gap-2"><Button variant="outline" onclick={() => (dropOpen = false)}>Cancel</Button><Button disabled={relBusy || !dropId} onclick={addToDrop}>{#if relBusy}<LoaderCircle size={14} class="animate-spin" />{/if} Add to drop</Button></div>
      </div>
    {/if}
  </Sheet>

  <Sheet bind:open={relOpen} title="Schedule release" description="{picked.length} product{picked.length === 1 ? '' : 's'} stay hidden until then, then go on sale tagged New.">
    <div class="space-y-5">
      <div class="grid grid-cols-2 gap-3">
        <div><label class={labelCls} for="rel-date">Date</label><DatePicker id="rel-date" bind:value={rel.date} min={shopDate(Date.now())} /></div>
        <div><label class={labelCls} for="rel-time">Time</label><TimePicker id="rel-time" bind:value={rel.time} /></div>
      </div>
      <div class="flex items-center justify-between gap-4">
        <div class="text-sm"><p class="font-medium">Show as “Coming soon”</p><p class="text-muted-foreground">Visitors see them but cannot buy yet. Off keeps them completely hidden.</p></div>
        <Switch bind:checked={rel.soon} aria-label="Show as Coming soon" />
      </div>
      <div class="flex items-center justify-between gap-4">
        <div class="text-sm"><p class="font-medium">Feature on the homepage when released</p><p class="text-muted-foreground">They lead the homepage's featured strip from release time.</p></div>
        <Switch bind:checked={rel.feature} aria-label="Feature on the homepage when released" />
      </div>
      <div class="flex justify-end gap-2 pt-2">
        <Button variant="outline" onclick={() => (relOpen = false)}>Cancel</Button>
        <Button disabled={relBusy || !rel.date} onclick={() => release('schedule')}>{#if relBusy}<LoaderCircle size={14} class="animate-spin" />{/if} Schedule</Button>
      </div>
    </div>
  </Sheet>
{:else}
  <div class="space-y-6 pb-24">
    <div>
      <button type="button" onclick={requestClose} class="mb-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"><ArrowLeft size={14} /> Products</button>
      <h1 class="text-2xl font-semibold tracking-tight">{isNew ? 'New product' : editing.name || 'Edit product'}</h1>
    </div>

    <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
      <div class="space-y-6 lg:col-span-2">
        <Card title="Details">
          <div class="space-y-4 p-6 pt-4">
            <div>
              <label for="edit-name" class={labelCls}>Name</label>
              <input id="edit-name" bind:value={editing.name} oninput={() => { if (isNew && !slugTouched) editing.slug = slugify(editing.name); }} class={inputCls} />
            </div>
            <div>
              <label for="edit-slug" class={labelCls}>Web address</label>
              <div class="flex items-center overflow-hidden rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring/40">
                <span class="shrink-0 select-none border-r border-input bg-muted/50 px-3 py-2 text-sm text-muted-foreground">{siteHost}/shop/</span>
                <input id="edit-slug" bind:value={editing.slug} placeholder={slugify(editing.name) || 'product-name'} maxlength="60" autocomplete="off" spellcheck="false"
                  oninput={() => (slugTouched = true)} onblur={() => (editing.slug = slugify(editing.slug))}
                  aria-describedby="edit-slug-hint" class="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm outline-none" />
              </div>
              <p id="edit-slug-hint" class="{hintCls} {slugClash ? '!text-destructive' : ''}">
                {#if slugClash}“{slugClash.name}” already uses this address, so a number will be added to keep it unique.
                {:else if isNew}Made from the name; change it if you like. Letters, numbers and hyphens only.
                {:else}Changing it is safe: the old address keeps working and forwards to the new one.{/if}
              </p>
            </div>
            <div>
              <label for="edit-desc" class={labelCls}>Description</label>
              <textarea id="edit-desc" bind:value={editing.description} rows={4} class={textareaCls}></textarea>
            </div>
          </div>
        </Card>

        <Card title="Media" description="The first image is the cover shown in the shop.">
          <div class="p-6 pt-4">
            <ImageUpload
              multi
              label=""
              values={editing.images}
              onChange={(urls) => (editing = { ...editing, images: urls, image: urls[0] || '' })}
            />
          </div>
        </Card>

        <Card title="Variants & stock" description="List the sizes and colours you offer, then set the stock for each combination.">
          <div class="space-y-6 p-6 pt-4">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label for="edit-sizes" class={labelCls}>Sizes</label>
                <input id="edit-sizes" value={sizesStr} oninput={updateSizes} placeholder="S, M, L, XL" class={inputCls} />
                <p class={hintCls}>Separate with commas.</p>
              </div>
              <div>
                <label for="edit-colors" class={labelCls}>Colours</label>
                <input id="edit-colors" value={colorsStr} oninput={updateColors} placeholder="Black, Bone, Washed Olive" class={inputCls} />
                {#if editing.colors.length}
                  <div class="mt-3 flex flex-wrap gap-2" role="group" aria-label="Colour swatches">
                    {#each editing.colors as color (color)}
                      <label class="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border py-1 pl-1 pr-3 text-xs hover:bg-muted/50" title="Pick the colour shown for {color}">
                        <span class="relative block h-6 w-6 overflow-hidden rounded-full border border-black/15" style="background:{swatchOf(color) || '#cccccc'}">
                          <input type="color" value={solidHex(color)} oninput={(e) => setSwatch(color, e.target.value)} class="absolute inset-0 h-full w-full cursor-pointer opacity-0" aria-label="Swatch colour for {color}" />
                        </span>{color}
                      </label>
                    {/each}
                  </div>
                  <p class={hintCls}>Shoppers see these swatches on product cards. We guess each colour from its name; click a swatch to change it.</p>
                {/if}
                <p class={hintCls}>Separate with commas. Leave empty if there is one colour.</p>
              </div>
            </div>

            <div>
              <div class="mb-2 flex items-center justify-between">
                <p class="text-sm font-medium">Stock {editing.sizes.length && editing.colors.length ? 'per size and colour' : editing.sizes.length ? 'per size' : editing.colors.length ? 'per colour' : ''}</p>
                <p class="text-sm text-muted-foreground">Total <span class="font-semibold text-foreground tabular-nums">{totalStock}</span></p>
              </div>
              {#if editing.sizes.length && editing.colors.length}
                <div class="overflow-x-auto rounded-lg border border-border">
                  <table class="w-full text-sm">
                    <thead class="bg-muted/50">
                      <tr class="border-b border-border">
                        <th class="{thCls} h-9"></th>
                        {#each editing.colors as color}<th class="{thCls} h-9 text-center">{color}</th>{/each}
                      </tr>
                    </thead>
                    <tbody>
                      {#each editing.sizes as size}
                        <tr class="border-b border-border/60 last:border-0">
                          <td class="px-4 py-2 font-medium">{size}</td>
                          {#each editing.colors as color}
                            <td class="px-4 py-2">
                              <input
                                type="number" min="0"
                                aria-label="Stock for {size} {color}"
                                value={variantStock(size, color)}
                                oninput={(e) => updateVariantStock(size, color, e.target.value)}
                                class="{inputCls} mx-auto h-8 w-20 text-center tabular-nums"
                              />
                            </td>
                          {/each}
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {:else}
                <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {#each (editing.sizes.length ? editing.sizes : editing.colors.length ? editing.colors : ['Stock']) as label}
                    <div>
                      <p class="mb-1 truncate text-xs text-muted-foreground">{label}</p>
                      <input
                        type="number" min="0"
                        aria-label="Stock for {label}"
                        value={variantStock(editing.sizes.length ? label : '', editing.colors.length ? label : '')}
                        oninput={(e) => updateVariantStock(editing.sizes.length ? label : '', editing.colors.length ? label : '', e.target.value)}
                        class="{inputCls} text-center tabular-nums"
                      />
                    </div>
                  {/each}
                </div>
              {/if}
            </div>

            {#if editing.colors.length}
              <div class="border-t border-border pt-6">
                <p class="text-sm font-medium">Per-colour images <span class="font-normal text-muted-foreground">(optional)</span></p>
                <p class="mb-4 mt-1 text-sm text-muted-foreground">Give a colour its own photos, shown once a shopper picks it. A colour left empty uses the general images above.</p>
                <div class="space-y-4">
                  {#each editing.colors as color}
                    <div class="rounded-lg border border-border p-4">
                      <ImageUpload multi label={color} values={colorImages(color)} onChange={(urls) => updateColorImages(color, urls)} />
                    </div>
                  {/each}
                </div>
              </div>
            {/if}
          </div>
        </Card>
      </div>

      <div class="space-y-6">
        <Card title="Pricing & category">
          <div class="space-y-4 p-6 pt-4">
            <div>
              <label for="edit-price" class={labelCls}>Price ({currency})</label>
              <input id="edit-price" type="number" min="0" step="0.01" bind:value={editing.price} class="{inputCls} tabular-nums" />
            </div>
            <div>
              <label for="edit-category" class={labelCls}>Category</label>
              <select id="edit-category" bind:value={editing.category} class={selectCls}>
                <option value="">Select a category…</option>
                {#each categories as cat}<option value={cat.id}>{cat.name}</option>{/each}
              </select>
            </div>
          </div>
        </Card>

        <Card title="Visibility">
          <div class="space-y-5 p-6 pt-4">
            <div class="flex items-center justify-between gap-4">
              <div class="text-sm"><p class="font-medium">Hidden from the shop</p><p class="text-muted-foreground">{editing.publishAtDropId ? 'A drop is holding this product. Manage it in the Drop planner.' : isScheduled(editing) ? 'Scheduled for release, so it follows that date. Change it from the product list.' : 'A draft. Nobody can see or buy it until you turn this off, or you schedule its release from the product list.'}</p></div>
              <Switch bind:checked={editing.hidden} disabled={Boolean(editing.publishAtDropId) || isScheduled(editing)} aria-label="Hidden from the shop" />
            </div>
            <div class="flex items-center justify-between gap-4">
              <div class="text-sm"><p class="font-medium">New arrival</p><p class="text-muted-foreground">Show the “New” tag</p></div>
              <Switch bind:checked={editing.isNew} aria-label="New arrival" />
            </div>
            <div class="flex items-center justify-between gap-4">
              <div class="text-sm"><p class="font-medium">Featured</p><p class="text-muted-foreground">Show on the homepage</p></div>
              <Switch bind:checked={editing.isFeatured} aria-label="Featured on homepage" />
            </div>
          </div>
        </Card>
      </div>
    </div>

    <div class="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background/90 backdrop-blur-sm md:left-60">
      <div class="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 md:px-8">
        <p class="text-sm {dirty ? 'text-foreground' : 'text-muted-foreground'}">{dirty ? 'You have unsaved changes' : 'No changes yet'}</p>
        <div class="flex gap-2">
          <Button variant="outline" disabled={saving} onclick={requestClose}>Cancel</Button>
          <Button disabled={saving} onclick={handleSave}>
            {#if saving}<LoaderCircle size={15} class="animate-spin" /> Saving…{:else}Save product{/if}
          </Button>
        </div>
      </div>
    </div>
  </div>
{/if}
